
























































































































































import {
  defineComponent,
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  useRouter,
  useContext,
} from "@nuxtjs/composition-api";
import useAdvancedSearch from "~/composables/useAdvancedSearch";
import { debounce } from "~/helpers/debounce";
import { useConfigStore } from "~/stores/extensionApi";
const CategoryProductCard = () =>
  import("~/modules/catalog/category/components/views/CategoryProductCard.vue");
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import { useClevertapStore } from "~/stores/clevertap";
import { useSearchProducts } from "~/composables";

export default defineComponent({
  name: "SearchPopup",
  components: {
    CategoryProductCard,
    SkeletonLoader,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const clevertapStore = useClevertapStore();
    const { fetchProducts, loading: productLoading } = useSearchProducts();
    const router = useRouter();
    const { app } = useContext();
    const { loading, error, searchResults, fetchSearchResults } =
      useAdvancedSearch();
    const searchQuery = ref("");
    const searchHistory = ref<string[]>([]);
    const storeConfig = useConfigStore();
    const productSuggestions = ref<any[]>([]);
    const SEARCH_HISTORY_KEY = "canifa_search_history";
    const SEARCH_HISTORY_LIMIT = 4;
    const slickProductItemsOptions = {
      slidesPerView: 2.3,
      slidesPerGroup: 2,
      spaceBetween: 16,
      centeredSlides: false,
      loop: false,
      pagination: { clickable: true },
      navigation: true,
      breakpoints: {
        640: {
          slidesPerView: 2.3,
          slidesPerGroup: 2,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 3.2,
          slidesPerGroup: 3,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 32,
        },
        1440: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 32,
        },
      },
    };

    const saveSearchHistory = () => {
      if (process.client) {
        try {
          localStorage.setItem(
            SEARCH_HISTORY_KEY,
            JSON.stringify(searchHistory.value)
          );
        } catch (err) {
          console.error("Error saving search history to localStorage:", err);
        }
      }
    };

    const loadSearchHistory = () => {
      if (process.client) {
        try {
          const savedHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
          if (savedHistory) {
            let history = JSON.parse(savedHistory);
            if (Array.isArray(history)) {
              searchHistory.value = history.slice(0, SEARCH_HISTORY_LIMIT);
            }
          }
        } catch (err) {
          console.error("Error loading search history from localStorage:", err);
        }
      }
    };

    const clearSearchHistory = () => {
      searchHistory.value = [];
      if (process.client) {
        try {
          localStorage.removeItem(SEARCH_HISTORY_KEY);
        } catch (err) {
          console.error(
            "Error clearing search history from localStorage:",
            err
          );
        }
      }
    };

    const clearSearch = () => {
      searchQuery.value = "";
    };

    const closePopup = () => {
      emit("close");
      searchQuery.value = "";
      document.body.style.overflow = "";
    };

    const debouncedSearch = debounce((query: string) => {
      if (query.trim().length > 0) {
        fetchSearchResults(query?.trim(), 1, 5);
      }
    }, 500);

    const fetchProductSuggestions = async (term = "") => {
      if (!term) {
        productSuggestions.value = [];
        return;
      }
      if (term.length < 2) {
        productSuggestions.value = [];
        return;
      }
      try {
        // Initialize search parameters with just the search term (no name filter)
        const searchParams = {
          search: term.trim(),
          pageSize: 6,
        };

        const { data, total } = await fetchProducts(searchParams);
        // Initialize search parameters with just the search term (no name filter)
        // const productList = await searchProducts(searchParams);
        productSuggestions.value = data || [];
        productUpdateCounter.value++;
        // Track search event with Clevertap after products are loaded
        if (
          process.client &&
          window.clevertap &&
          productSuggestions.value?.length > 0
        ) {
          trackSearchEvent(term, productSuggestions.value, total);
        }
      } catch (err) {
        productSuggestions.value = [];
      }
    };

    // Function to track search event with Clevertap
    const trackSearchEvent = (term: string, items: any[], totalCount: any) => {
      if (!items || items.length === 0) return;
      const eventData: Record<string, any> = {
        query: term,
        search_result_number: totalCount,
      };
      // Add product IDs and URLs to tracking data
      items.forEach((product, index) => {
        const productNumber = index + 1;
        eventData[`product_id_${productNumber}`] = product.sku || "";
        // Create full product URL
        const productUrl = product.url_key
          ? `${window.location.origin}/${product.url_key}${
              product.url_suffix || ""
            }`
          : "";
        eventData[`product_url_${productNumber}`] = productUrl;
      });
      // Send event to Clevertap
      clevertapStore.productSearchCleverTap({
        data: eventData,
        clevertap: window.clevertap,
      });
    };

    const debouncedProductSearch = debounce((query: string) => {
      fetchProductSuggestions(query);
    }, 1000);

    const handleSearchInput = () => {
      // Clear immediately when typing
      productSuggestions.value = [];
      shouldShowNoResults.value = false;

      debouncedSearch(searchQuery.value);

      if (searchQuery.value && searchQuery.value.trim().length >= 2) {
        debouncedProductSearch(searchQuery.value);
      }
    };

    watch(
      () => searchQuery.value,
      (newQuery) => {
        // Clear product suggestions immediately when query changes
        productSuggestions.value = [];
        
        if (newQuery.trim().length < 2) {
          return;
        }

        debouncedProductSearch(newQuery);
      }
    );

    const navigateToSearchPage = (term: string) => {
      if (term && term.trim().length > 0) {
        if (!searchHistory.value.includes(term)) {
          searchHistory.value.unshift(term);
          if (searchHistory.value.length > SEARCH_HISTORY_LIMIT) {
            searchHistory.value = searchHistory.value.slice(
              0,
              SEARCH_HISTORY_LIMIT
            );
          }
          saveSearchHistory();
        } else {
          searchHistory.value = [
            term,
            ...searchHistory.value.filter((item) => item !== term),
          ];
          if (searchHistory.value.length > SEARCH_HISTORY_LIMIT) {
            searchHistory.value = searchHistory.value.slice(
              0,
              SEARCH_HISTORY_LIMIT
            );
          }
          saveSearchHistory();
        }
        closePopup();
        router.push({
          path: "/search",
          query: { q: term },
        });
      }
    };

    const selectSearchResult = (result: any) => {
      const term = result.content;
      searchQuery.value = term;
      navigateToSearchPage(term);
    };

    const applySearch = (query: string) => {
      searchQuery.value = query;
      // Focus on the search input after setting the query
      setTimeout(() => {
        const searchInput = document.getElementById("search-popup__form-input");
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    };

    const normalizeText = (text: string) => {
      return text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    };

    const highlightMatchedText = (
      text: string | null | undefined,
      query: string | null | undefined
    ) => {
      if (!text || !query) return text || "";
      const normalizedSearchQuery = normalizeText(query);
      const queryWords = normalizedSearchQuery.split(/\s+/);
      const originalWords = text.split(/\s+/);
      let resultHTML = "";
      let textIndex = 0;
      for (let word of originalWords) {
        const normalizedWord = normalizeText(word);
        let matched = false;
        for (let query of queryWords) {
          if (normalizedWord.includes(query)) {
            const startIndex = normalizedWord.indexOf(query);
            const endIndex = startIndex + query.length;
            resultHTML += "<span>" + word.slice(0, startIndex) + "</span>";
            resultHTML +=
              "<strong>" + word.slice(startIndex, endIndex) + "</strong>";
            resultHTML += "<span>" + word.slice(endIndex) + "</span>";
            matched = true;
            break;
          }
        }
        if (!matched) {
          resultHTML += "<span>" + word + "</span>";
        }
        textIndex += word.length;
        if (textIndex < text.length) resultHTML += " ";
      }
      return resultHTML;
    };

    const mostSearchRaw = computed(() => storeConfig.getMostSearch || "");

    const popularKeywords = computed(() => {
      if (
        typeof mostSearchRaw.value === "string" &&
        mostSearchRaw.value.trim() !== ""
      ) {
        return mostSearchRaw.value.split(",").map((keyword) => keyword.trim());
      } else if (Array.isArray(mostSearchRaw.value)) {
        return mostSearchRaw.value;
      }
      return ["Áo phông", "Hoddie", "Áo khoác", "6LB22W001"];
    });

    const shouldShowNoResults = ref(false);
    let noResultsTimer: ReturnType<typeof setTimeout> | null = null;

    const handleNoResultsDisplay = () => {
      if (noResultsTimer !== null) {
        clearTimeout(noResultsTimer);
        noResultsTimer = null;
      }
      shouldShowNoResults.value = false;
      if (
        searchQuery.value &&
        searchQuery.value.trim() !== "" &&
        searchResults.value.length === 0
      ) {
        noResultsTimer = setTimeout(() => {
          shouldShowNoResults.value = true;
        }, 200);
      }
    };

    watch([() => searchResults.value, () => loading.value], () => {
      handleNoResultsDisplay();
    });

    watch(
      () => searchQuery.value,
      (newQuery) => {
        if (noResultsTimer !== null) {
          clearTimeout(noResultsTimer);
          noResultsTimer = null;
        }
        shouldShowNoResults.value = false;
      }
    );

    watch(
      () => props.isOpen,
      (newValue) => {
        if (newValue) {
          setTimeout(() => {
            const searchInput = document.getElementById(
              "search-popup__form-input"
            );
            if (searchInput) {
              searchInput.focus();
            }
          }, 100);
        }
      }
    );

    watch(
      () => searchQuery.value,
      (newQuery, oldQuery) => {
        // Clear product suggestions immediately when query changes
        productSuggestions.value = [];
        
        if (!newQuery || newQuery.trim() === "") {
          searchResults.value = [];
          return;
        }
        if (newQuery.trim().length < 2) {
          searchResults.value = [];
          return;
        }
        if (!oldQuery || newQuery.trim() !== oldQuery.trim()) {
          debouncedSearch(newQuery);
        }
      }
    );

    onMounted(() => {
      loadSearchHistory();
      // Setup route change listener to close popup when route changes
      const unregisterRouteGuard = router.afterEach(() => {
        if (props.isOpen) {
          closePopup();
        }
      });

      // Cleanup the route listener when component is unmounted
      onBeforeUnmount(() => {
        if (unregisterRouteGuard) {
          unregisterRouteGuard();
        }
      });
    });

    watch(
      () => props.isOpen,
      (newValue) => {
        if (newValue) {
          setTimeout(() => {
            const searchInput = document.getElementById(
              "search-popup__form-input"
            );
            if (searchInput) {
              searchInput.focus();
            }
            if (
              !productSuggestions.value ||
              productSuggestions.value.length === 0
            ) {
              fetchProductSuggestions();
            }
          }, 100);
        }
      }
    );

    const productUpdateCounter = ref(0);
    watch(
      productSuggestions,
      (newValue) => {
        productUpdateCounter.value++;
      },
      { deep: true }
    );

    // Track product click from search results
    const trackProductClick = (product, position) => {
      try {
        // Set search context in CleverTap store
        clevertapStore.setInforFromSearch({
          is_search_result: true,
          seach_result_query: searchQuery.value,
          position: position,
        });
        // Add actionFromTo to track that user is coming from search
        clevertapStore.actionFromTo({
          from_location: "search",
        });
      } catch (error) {
        console.error("Error tracking product click:", error);
      }
    };

    return {
      searchQuery,
      searchHistory,
      loading,
      error,
      searchResults,
      closePopup,
      clearSearch,
      clearSearchHistory,
      handleSearchInput,
      selectSearchResult,
      applySearch,
      normalizeText,
      highlightMatchedText,
      popularKeywords,
      shouldShowNoResults,
      slickProductItemsOptions,
      productSuggestions,
      fetchProductSuggestions,
      debouncedProductSearch,
      productLoading,
      productUpdateCounter,
      trackSearchEvent,
      navigateToSearchPage,
      trackProductClick,
    };
  },
});
